/**
 * Service layer for Game Master Skill Progression Analysis
 * Wraps the /api/gamemaster/skill-calculator/analyze-progression/ endpoint
 * Provides skill scaling visualization across stat ranges
 */

import { GameMasterSkillCalculatorGameApi } from '@/api/backendService.js';
import { ImpactViolationTypeEnum } from '@/api/dx-backend';
import { EventEmitter } from 'events';

class GameMasterSkillProgressionService extends EventEmitter {
  constructor() {
    super();
    this.api = GameMasterSkillCalculatorGameApi;
    this.lastResult = null;
    this.isAnalyzing = false;
  }

  /**
   * Analyze skill progression across stat range
   * @param {Object} request - Analysis request
   * @param {number} request.skill_id - Skill ID to analyze
   * @param {string} request.primary_stat - Stat to vary (e.g., "Flow Manipulation")
   * @param {number} request.min_value - Minimum stat value (default: 0)
   * @param {number} request.max_value - Maximum stat value (default: 150)
   * @param {number} request.step - Step size (default: 5)
   * @param {number} request.baseline_minimum - Baseline for minimum curve (default: 10)
   * @param {number} request.baseline_median - Baseline for median curve (default: 50)
   * @param {number} request.baseline_maximum - Baseline for maximum curve (default: 100)
   * @returns {Promise<Object>} Progression analysis result
   */
  async analyzeProgression(request) {
    try {
      this.isAnalyzing = true;
      this.emit('analysisStarted', request);

      const response = await this.api.gamemasterSkillCalculatorAnalyzeProgressionCreate({
        skill_id: request.skill_id,
        primary_stat: request.primary_stat,
        min_value: request.min_value || 0,
        max_value: request.max_value || 150,
        step: request.step || 5,
        baseline_minimum: request.baseline_minimum || 10,
        baseline_median: request.baseline_median || 50,
        baseline_maximum: request.baseline_maximum || 100
      });

      this.lastResult = response.data;
      this.emit('analysisCompleted', response.data);

      return response.data;
    } catch (error) {
      console.error('Skill progression analysis failed:', error);
      this.emit('analysisFailed', error);
      throw error;
    } finally {
      this.isAnalyzing = false;
    }
  }

  /**
   * Get the last analysis result
   * @returns {Object|null} Last result or null
   */
  getLastResult() {
    return this.lastResult;
  }

  /**
   * Check if analysis is in progress
   * @returns {boolean} True if analyzing
   */
  isInProgress() {
    return this.isAnalyzing;
  }

  /**
   * Clear the last result
   */
  clearResult() {
    this.lastResult = null;
    this.emit('resultCleared');
  }

  /**
   * Extract chart data from progression result
   * @param {Object} progressionData - Progression analysis result from API
   * @param {string} impactFilter - Filter by impact kind (e.g., "DAMAGE", "HEAL", or "ALL")
   * @returns {Object} Chart.js compatible data structure
   */
  extractChartData(progressionData, impactFilter = 'ALL') {
    if (!progressionData || !progressionData.progression) {
      return null;
    }

    const { progression, primary_stat, skill } = progressionData;

    // Extract stat values for X-axis
    const labels = progression.map(point => point.stat_value);

    // Build datasets for each impact and curve combination
    const datasets = [];
    const impactTypes = new Set();

    // Collect all unique impacts
    if (progression.length > 0) {
      const firstPoint = progression[0];
      firstPoint.minimum_curve.forEach(impact => {
        if (impactFilter === 'ALL' || impact.kind === impactFilter) {
          impactTypes.add(`${impact.kind}-${impact.violation}`);
        }
      });
    }

    // Create datasets for each impact type with 3 curves
    Array.from(impactTypes).forEach((impactKey) => {
      const [kind, violation] = impactKey.split('-');

      // Color scheme based on violation type using enum
      const colors = this._getColorForViolation(violation);

      // Minimum curve (dashed)
      datasets.push({
        label: `${kind} ${violation} (Min)`,
        data: progression.map(point => {
          const impact = point.minimum_curve.find(i => i.kind === kind && i.violation === violation);
          return impact ? impact.value : 0;
        }),
        borderColor: colors.light,
        backgroundColor: colors.light + '20',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        tension: 0.4
      });

      // Median curve (solid)
      datasets.push({
        label: `${kind} ${violation} (Med)`,
        data: progression.map(point => {
          const impact = point.median_curve.find(i => i.kind === kind && i.violation === violation);
          return impact ? impact.value : 0;
        }),
        borderColor: colors.main,
        backgroundColor: colors.main + '40',
        borderWidth: 3,
        pointRadius: 0,
        tension: 0.4,
        fill: false
      });

      // Maximum curve (dotted)
      datasets.push({
        label: `${kind} ${violation} (Max)`,
        data: progression.map(point => {
          const impact = point.maximum_curve.find(i => i.kind === kind && i.violation === violation);
          return impact ? impact.value : 0;
        }),
        borderColor: colors.dark,
        backgroundColor: colors.dark + '20',
        borderWidth: 2,
        borderDash: [2, 2],
        pointRadius: 0,
        tension: 0.4
      });
    });

    return {
      labels,
      datasets,
      meta: {
        skillName: skill.name,
        schoolName: skill.school_name,
        primaryStat: primary_stat,
        impactCount: impactTypes.size
      }
    };
  }

  /**
   * Get color scheme for violation type using generated enum
   * @private
   */
  _getColorForViolation(violation) {
    // Use the ImpactViolationTypeEnum enum values
    const colorMap = {
      [ImpactViolationTypeEnum.Physical]: { light: '#ff9999', main: '#ff6b6b', dark: '#cc5555' },
      [ImpactViolationTypeEnum.Heat]: { light: '#ffaa77', main: '#ff8844', dark: '#cc6633' },
      [ImpactViolationTypeEnum.Cold]: { light: '#88ccff', main: '#5599ff', dark: '#4477cc' },
      [ImpactViolationTypeEnum.Mental]: { light: '#bb99ff', main: '#9966ff', dark: '#7744cc' },
      [ImpactViolationTypeEnum.Energy]: { light: '#ffee77', main: '#ffdd44', dark: '#ccaa33' },
      [ImpactViolationTypeEnum.Light]: { light: '#ffffaa', main: '#ffff66', dark: '#cccc44' },
      [ImpactViolationTypeEnum.Darkness]: { light: '#999999', main: '#666666', dark: '#333333' },
      [ImpactViolationTypeEnum.None]: { light: '#00d9ff', main: '#00a8cc', dark: '#007799' }
    };

    return colorMap[violation] || colorMap[ImpactViolationTypeEnum.None];
  }
}

export const gameMasterSkillProgressionService = new GameMasterSkillProgressionService();